import { type ChecklistItemInput, ChecklistItemsInput, checklistWeights } from '@apm/contracts';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { randomUuid } from '../../lib/uuid';

/**
 * Ô nội dung cao theo số dòng của chính nó — mục có Shift+Enter thì cao hơn,
 * mục một dòng giữ đúng chiều cao `h-8` như trước.
 */
function fitHeight(element: HTMLTextAreaElement | null): void {
  if (!element) return;
  element.style.height = 'auto';
  element.style.height = `${element.scrollHeight}px`;
}

export function TaskChecklistEditor({
  items,
  onChange,
}: {
  readonly items: ChecklistItemInput[];
  readonly onChange: (items: ChecklistItemInput[] | null) => void;
}) {
  const { t } = useTranslation();
  const weights = checklistWeights(items);
  const valid = ChecklistItemsInput.safeParse(items).success;
  const update = (index: number, patch: Partial<ChecklistItemInput>) =>
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  /**
   * Mục vừa được Enter tạo ra — để đưa con trỏ sang đó.
   *
   * Giữ **id** chứ không giữ chỉ số: xoá một mục ở trên làm mọi chỉ số phía sau
   * lùi một bậc, và tiêu điểm sẽ nhảy sang mục khác. Id thì không đổi.
   */
  const focusId = useRef<string | null>(null);

  useEffect(() => {
    if (focusId.current === null) return;
    const target = focusId.current;
    focusId.current = null;
    /*
      Tìm theo `data-item-id` thay vì giữ một `ref` cho mỗi ô: số ô đổi theo
      từng lần gõ, nên một mảng ref phải tự dọn theo — và mảng ấy sai lúc nào thì
      tiêu điểm nhảy lung tung lúc đó.
    */
    document.querySelector<HTMLTextAreaElement>(`textarea[data-item-id="${target}"]`)?.focus();
  }, [items]);

  /**
   * Enter ở ô nội dung: chèn một mục **ngay dưới** và đưa con trỏ sang đó — APM-CHECK-b.
   *
   * Chèn ngay dưới chứ không thêm vào cuối: người dùng đang gõ ở giữa danh sách
   * thì thứ họ muốn là mục kế tiếp ở đúng chỗ ấy, không phải nhảy xuống đáy.
   *
   * ⚠️ Chặn hành vi mặc định: ô này nằm trong `<form>` của hộp thoại sửa task,
   * và Enter trong một form là **gửi form**. Không chặn thì mỗi lần xuống dòng
   * là một lần lưu task và đóng hộp thoại.
   *
   * **Shift+Enter** mới là xuống dòng *trong* mục (người dùng yêu cầu
   * 2026-10-01) — cùng quy ước với ô chat, nên ô là `<textarea>` chứ không còn
   * là `<input>` một dòng.
   */
  const addAfter = (index: number): void => {
    if (items.length >= 100) return;

    const id = randomUuid();
    focusId.current = id;
    onChange([
      ...items.slice(0, index + 1),
      { id, text: '', weight: null },
      ...items.slice(index + 1),
    ]);
  };

  return (
    <section className="border-t border-input p-3" aria-label={t('checklist.title')}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h4 className="text-sm font-medium">{t('checklist.title')}</h4>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-xs text-muted-foreground hover:text-destructive"
        >
          {t('checklist.remove')}
        </button>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">{t('checklist.weightHelp')}</p>
      <ol className="flex flex-col gap-2">
        {items.map((item, index) => (
          // `items-start`: mục nhiều dòng thì trọng số và nút xoá đứng ở dòng đầu.
          <li key={item.id} className="flex items-start gap-2">
            <span
              aria-hidden
              className="mt-1.5 size-5 shrink-0 rounded-full border-[1.5px] border-muted-foreground/50"
            />
            <textarea
              ref={fitHeight}
              rows={1}
              aria-label={t('checklist.itemLabel', { number: index + 1 })}
              data-item-id={item.id}
              value={item.text}
              onChange={(event) => {
                fitHeight(event.target);
                update(index, { text: event.target.value });
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' || event.shiftKey) return;
                // Bàn phím tiếng Việt: Enter chốt một chuỗi đang gõ dở của IME,
                // và lúc đó nó **không** phải là "xuống dòng".
                if (event.nativeEvent.isComposing) return;
                event.preventDefault();
                addAfter(index);
              }}
              maxLength={500}
              placeholder={t('checklist.itemPlaceholder')}
              className="min-h-8 min-w-0 flex-1 resize-none overflow-hidden rounded-sm border border-input bg-background px-2 py-1.5 text-sm leading-5"
            />
            <input
              type="number"
              min="0.01"
              max="100"
              step="0.01"
              aria-label={t('checklist.weightLabel', { number: index + 1 })}
              value={item.weight ?? ''}
              placeholder={t('checklist.auto')}
              onChange={(event) =>
                update(index, {
                  weight: event.target.value === '' ? null : Number(event.target.value),
                })
              }
              className="h-8 w-20 rounded-sm border border-input bg-background px-2 text-sm"
            />
            <span className="mt-2 w-14 shrink-0 text-right text-xs tabular-nums">
              {Math.max(0, weights[index] ?? 0).toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })}
              %
            </span>
            <button
              type="button"
              aria-label={t('checklist.removeItem', { number: index + 1 })}
              onClick={() =>
                onChange(items.length === 1 ? null : items.filter((_, i) => i !== index))
              }
              className="mt-1 p-1 text-muted-foreground hover:text-destructive rounded-full glass-hover"
            >
              <Trash2 className="size-4" />
            </button>
          </li>
        ))}
      </ol>
      {!valid && (
        <p role="alert" className="mt-2 text-xs text-destructive">
          {t('checklist.invalid')}
        </p>
      )}
      <button
        type="button"
        disabled={items.length >= 100}
        onClick={() => onChange([...items, { id: randomUuid(), text: '', weight: null }])}
        className="mt-3 inline-flex items-center gap-1 text-xs text-primary disabled:opacity-40"
      >
        <Plus className="size-4" />
        {t('checklist.addItem')}
      </button>
    </section>
  );
}
